import React, { useMemo } from "react";
import { Input, Textarea, Tooltip, TooltipContent } from "@panda0/panda0-ui";
import { cn } from "@panda0/panda0-utils";
import {
  Briefcase,
  Building,
  Clock,
  DollarSign,
  EuroIcon,
  ExternalLink,
  FileText,
  GraduationCap,
  HomeIcon,
  Info,
  Laptop,
  PoundSterlingIcon,
} from "lucide-react";
import { Controller, useFormContext } from "react-hook-form";

import { FormData } from "../types";

const JobDetailsEditor: React.FC = () => {
  // Work type options with icons
  const workTypeOptions = useMemo(
    () => [
      {
        value: "Full-time",
        label: "Full-time",
        icon: <Briefcase className="h-4 w-4" />,
      },
      {
        value: "Part-time",
        label: "Part-time",
        icon: <Clock className="h-4 w-4" />,
      },
      {
        value: "Contract",
        label: "Contract",
        icon: <FileText className="h-4 w-4" />,
      },
      {
        value: "Freelance",
        label: "Freelance",
        icon: <ExternalLink className="h-4 w-4" />,
      },
      {
        value: "Internship",
        label: "Internship",
        icon: <GraduationCap className="h-4 w-4" />,
      },
    ],
    []
  );

  // Work mode options with icons
  const workModeOptions = useMemo(
    () => [
      {
        value: "Remote",
        label: "Remote",
        icon: <HomeIcon className="h-4 w-4" />,
      },
      {
        value: "On-site",
        label: "On-site",
        icon: <Building className="h-4 w-4" />,
      },
      {
        value: "Hybrid",
        label: "Hybrid",
        icon: <Laptop className="h-4 w-4" />,
      },
    ],
    []
  );

  // Currency options with icons
  const currencyOptions = useMemo(
    () => [
      {
        value: "USD",
        label: "USD",
        icon: <DollarSign className="h-4 w-4" />,
        symbol: "$",
      },
      {
        value: "EUR",
        label: "EUR",
        icon: <EuroIcon className="h-4 w-4" />,
        symbol: "€",
      },
      {
        value: "GBP",
        label: "GBP",
        icon: <PoundSterlingIcon className="h-4 w-4" />,
        symbol: "£",
      },
      { value: "INR", label: "INR", icon: "₹", symbol: "₹" }, // Using text for INR since no built-in icon
    ],
    []
  );

  const { control, register, watch, setValue } = useFormContext<FormData>();
  const jobDetails = watch("jobDetails");

  // Helper to get the selected currency object
  const selectedCurrency =
    currencyOptions.find(
      (c) => c.value === (jobDetails?.JOBINFORMATION_SALARYCURRENCY || "INR")
    ) || currencyOptions[0];

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">
        Configure job details that will be displayed to candidates.
      </p>

      {/* Salary Range */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="block font-medium">Salary Range</label>
          <Tooltip
            content={
              <TooltipContent
                title="Including salary range increases application rates by up to 30%
            and improves candidate quality."
              />
            }
          >
            <div className="flex items-center text-xs text-gray-500 cursor-help">
              <Info className="h-3.5 w-3.5 mr-1" />
              <span>Why include salary?</span>
            </div>
          </Tooltip>
        </div>

        {/* Currency Selector */}
        <div className="flex gap-2">
          {currencyOptions.map((option) => (
            <Controller
              name="jobDetails.JOBINFORMATION_SALARYCURRENCY"
              control={control}
              render={({ field }) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => field.onChange(option.value)}
                  className={cn(
                    "flex items-center justify-center w-12 h-10 rounded-md border transition-all",
                    jobDetails?.JOBINFORMATION_SALARYCURRENCY === option.value
                      ? "bg-black border-black text-white font-medium"
                      : "border-gray-300 hover:border-gray-400 text-gray-700 hover:bg-gray-50"
                  )}
                >
                  {typeof option.icon === "string" ? option.icon : option.icon}
                </button>
              )}
            />
          ))}
        </div>

        {/* Min-Max Range */}
        <div className="relative mt-2">
          <div className="flex items-center bg-gray-50 border rounded-md p-2 gap-2">
            <div className="flex items-center flex-1">
              <div className="absolute left-6 text-gray-500 z-10">
                {selectedCurrency.symbol}
              </div>
              <Controller
                name="jobDetails.JOBINFORMATION_SALARYMIN"
                control={control}
                render={({ field }) => (
                  <Input
                    placeholder="Min"
                    className="pl-7 flex-1"
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    type="number"
                    min="0"
                  />
                )}
              />
            </div>

            <div className="text-gray-400">to</div>

            <div className="flex items-center flex-1">
              <div className="absolute right-[calc(30%+3rem)] text-gray-500 z-10">
                {selectedCurrency.symbol}
              </div>
              <Controller
                name="jobDetails.JOBINFORMATION_SALARYMAX"
                control={control}
                render={({ field }) => (
                  <Input
                    placeholder="Max"
                    className="pl-7 flex-1"
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    type="number"
                    min="0"
                  />
                )}
              />
            </div>
          </div>

          <div className="mt-2 text-xs text-gray-500 flex items-center">
            <div className="flex-1">
              {jobDetails?.JOBINFORMATION_SALARYMIN &&
              jobDetails.JOBINFORMATION_SALARYMAX ? (
                <span className="font-medium text-gray-600">
                  Range: {selectedCurrency.symbol}
                  {parseInt(
                    jobDetails.JOBINFORMATION_SALARYMIN
                  ).toLocaleString()}{" "}
                  -{selectedCurrency.symbol}
                  {parseInt(
                    jobDetails.JOBINFORMATION_SALARYMAX
                  ).toLocaleString()}
                  {selectedCurrency.value === "INR" ? " LPA" : ""}
                </span>
              ) : (
                <span>Enter min and max values</span>
              )}
            </div>

            <div className="text-right">
              {jobDetails?.JOBINFORMATION_SALARYMIN &&
                jobDetails.JOBINFORMATION_SALARYMAX && (
                  <span>
                    {Math.round(
                      ((parseInt(jobDetails.JOBINFORMATION_SALARYMAX) -
                        parseInt(jobDetails.JOBINFORMATION_SALARYMIN)) /
                        parseInt(jobDetails.JOBINFORMATION_SALARYMIN)) *
                        100
                    )}
                    % difference
                  </span>
                )}
            </div>
          </div>
        </div>
      </div>

      {/* Work Type */}
      <div className="space-y-3">
        <label className="block font-medium">Work Type</label>
        <div className="flex flex-wrap gap-2">
          {workTypeOptions.map((option) => (
            <Controller
              name="jobDetails.JOBINFORMATION_WORKTYPE"
              control={control}
              render={({ field }) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => field.onChange(option.value)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border transition-all",
                    jobDetails?.JOBINFORMATION_WORKTYPE === option.value
                      ? "bg-black border-black text-white font-medium"
                      : "border-gray-300 hover:border-gray-400 text-gray-700 hover:bg-gray-50"
                  )}
                >
                  {option.icon}
                  <span>{option.label}</span>
                </button>
              )}
            />
          ))}
        </div>
      </div>

      {/* Work Mode */}
      <div className="space-y-3">
        <label className="block font-medium">Work Mode</label>
        <div className="flex gap-2">
          {workModeOptions.map((option) => (
            <Controller
              name="jobDetails.JOBINFORMATION_WORKMODE"
              control={control}
              render={({ field }) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => field.onChange(option.value)}
                  className={cn(
                    "flex flex-1 items-center justify-center gap-1.5 px-3 py-2 rounded-md border transition-all",
                    jobDetails?.JOBINFORMATION_WORKMODE === option.value
                      ? "bg-black border-black text-white font-medium"
                      : "border-gray-300 hover:border-gray-400 text-gray-700 hover:bg-gray-50"
                  )}
                >
                  {option.icon}
                  <span>{option.label}</span>
                </button>
              )}
            />
          ))}
        </div>
      </div>

      {/* Work Location */}
      <div className="space-y-2">
        <label className="block font-medium">Work Location</label>
        <Controller
          name="jobDetails.JOBINFORMATION_WORKLOCATION"
          control={control}
          render={({ field }) => (
            <Input
              placeholder="e.g., New York, NY"
              value={field.value || ""}
              onChange={(e) => field.onChange(e.target.value)}
            />
          )}
        />
      </div>

      {/* Short Description */}
      <div className="space-y-2">
        <label className="block font-medium">Short Description</label>
        <Controller
          name="jobDetails.JOBINFORMATION_SHORTDESCRIPTION"
          control={control}
          render={({ field }) => (
            <Textarea
              placeholder="Brief summary of the position for social media/summary etc"
              value={field.value || ""}
              onChange={(e) => field.onChange(e.target.value)}
            />
          )}
        />
      </div>

      {/* Perks & Benefits */}
      <div className="space-y-2">
        <label className="block font-medium">Perks & Benefits</label>
        <Controller
          name="jobDetails.JOBINFORMATION_PERKSBENEFITS"
          control={control}
          render={({ field }) => (
            <Textarea
              placeholder="List the benefits and perks of this position"
              value={field.value || ""}
              onChange={(e) => field.onChange(e.target.value)}
            />
          )}
        />
      </div>

      {/* Full Job Description */}
      <div className="space-y-2">
        <label className="block font-medium">Full Job Description</label>
        <Controller
          name="jobDetails.JOBINFORMATION_OGDESCRIPTION"
          control={control}
          render={({ field }) => (
            <Textarea
              placeholder="Job description"
              className="min-h-[200px]"
              value={field.value || ""}
              onChange={(e) => field.onChange(e.target.value)}
            />
          )}
        />
      </div>
    </div>
  );
};

export default React.memo(JobDetailsEditor);
